//
// DISCLAIMER
//
// Copyright 2026 ArangoDB GmbH, Cologne, Germany
//

import { Component } from "react";
import { Header, Loader, Segment, Table } from "semantic-ui-react";
import apiClients from "../../api/apiclients";
import { Event as ApiEvent, EventList as ApiEventList } from "../../api/lib";
import { ErrorMessage, LoaderBox, PagingButtons } from "../../ui/lib";
import { DateTimePopupWithUTCAndLocalTime } from "../../util/dateAndTimeUtils/DateTime";
import { CD_ROBOT_EVENT_TYPES } from "./auditDisplay";

const eventSummary = (evt: ApiEvent): string => {
  const payload = evt.payload || {};
  if (evt.type === "internal-cd-robot.run.finished") {
    const parts = [
      payload.outcome && `outcome: ${payload.outcome}`,
      payload.branch && `branch: ${payload.branch}`,
      payload.started_by_email && `by ${payload.started_by_email}`,
      payload.failure_summary,
    ].filter(Boolean);
    return parts.join(" · ") || payload.run_id || "—";
  }
  if (evt.type === "internal-cd-robot.schedule.updated") {
    const parts = [payload.changed_by_email && `by ${payload.changed_by_email}`, payload.new_start_schedule && `start: ${payload.new_start_schedule}`].filter(
      Boolean
    );
    return parts.join(" · ") || "—";
  }
  return evt.reason || "—";
};

interface ICDRobotEventsState {
  list?: ApiEventList;
  page: number;
  pageSize: number;
  loading: boolean;
  errorMessage?: string;
}

export class CDRobotEvents extends Component<{}, ICDRobotEventsState> {
  state: ICDRobotEventsState = {
    page: 0,
    pageSize: 15,
    loading: false,
  };

  reload = async () => {
    this.setState({ loading: true, errorMessage: undefined });
    try {
      const list = await apiClients.idashboardClient.ListAllEvents({
        options: {
          page: this.state.page,
          page_size: this.state.pageSize,
        },
        types: CD_ROBOT_EVENT_TYPES,
        sort_descending: true,
      });
      this.setState({ list, loading: false });
    } catch (e) {
      this.setState({ loading: false, errorMessage: `Failed to load CD Robot events: ${e}` });
    }
  };

  componentDidMount() {
    this.reload();
  }

  onNextPage = () => {
    this.setState((prev) => ({ page: prev.page + 1 }), this.reload);
  };

  onPreviousPage = () => {
    this.setState((prev) => ({ page: Math.max(0, prev.page - 1) }), this.reload);
  };

  render() {
    const { list, loading, errorMessage, page, pageSize } = this.state;
    const items = list?.items || [];

    return (
      <Segment>
        <Header sub>Audit event history</Header>
        <ErrorMessage active={!!errorMessage} message={errorMessage} />
        <Table compact="very" celled>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Type</Table.HeaderCell>
              <Table.HeaderCell>Summary</Table.HeaderCell>
              <Table.HeaderCell>Created</Table.HeaderCell>
              <Table.HeaderCell>
                <PagingButtons page={page} count={items.length} pageSize={pageSize} onNextPage={this.onNextPage} onPreviousPage={this.onPreviousPage} />
                <LoaderBox>
                  <Loader size="mini" active={loading} inline />
                </LoaderBox>
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {items.map((evt) => (
              <Table.Row key={evt.id}>
                <Table.Cell>{evt.type}</Table.Cell>
                <Table.Cell>{eventSummary(evt)}</Table.Cell>
                <Table.Cell>{evt.created_at ? <DateTimePopupWithUTCAndLocalTime dateTime={evt.created_at} label="Created at" /> : "—"}</Table.Cell>
                <Table.Cell />
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
        {!loading && items.length === 0 && <div style={{ opacity: 0.7, marginTop: "0.5rem" }}>No CD Robot audit events yet.</div>}
      </Segment>
    );
  }
}
